import 'react-datepicker/dist/react-datepicker.css';

import CloseIcon from '@assets/images/common/icon/close.svg';
import PdfIcon from '@assets/images/common/icon/pdf.svg';
import SearchIcon from '@assets/images/common/icon/search/search.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { articleApiClient } from '@src/api/tenant/article/articleApiClient';
import { brandColors } from '@src/assets/colors/brandColors';
import SvgIconClose from '@src/assets/images/common/icon/close/SvgIconClose';
import { firstError } from '@src/utils/errorMessageUtils';
import { ja } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';

interface ErrorMessageProps {
  children: React.ReactNode;
}

interface InputProps {
  countChange?: (count: number) => void;
  placeholder?: string;
  onEnterPress?: (value: string) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
}

interface TagProps {
  text: string;
  index: number;
  onClick: (index: number) => void;
}

interface SelectProps {
  items: { id: string; name: string }[];
  onSelect?: (id: string) => void;
  initialValue?: string;
}

interface CheckProps {
  onCheck: () => void;
  onUncheck: () => void;
  checked?: boolean;
}

interface ImageUploaderProps {
  text: string;
  onImageUpload: (url: string) => void;
  onError: (error: string) => void;
}

interface PdfUploaderProps {
  text: string;
  type: string;
  onPdfUpload: (pdfUrl: string, name: string) => void;
  onError: (error: string) => void;
}

function ErrorMessage({ children }: ErrorMessageProps) {
  return <p className='text-xs text-warning-500'>{children}</p>;
}

interface InputCalenderProps {
  setPublishedAt: (date: string) => void;
  initialValue?: string;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;

const InputCalender: React.FC<InputCalenderProps> = ({ setPublishedAt, initialValue }) => {
  const [startDate, setStartDate] = useState<Date | null>(
    initialValue ? new Date(initialValue) : null,
  );

  useEffect(() => {
    if (initialValue) {
      const date = new Date(initialValue);
      setStartDate(date);
      setPublishedAt(date.toLocaleDateString('en-CA'));
    }
  }, [initialValue, setPublishedAt]);

  const handleDateChange = (date: Date | null) => {
    setStartDate(date);
    if (date) {
      const localDateString = date.toLocaleDateString('en-CA');
      setPublishedAt(localDateString);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    event.preventDefault();
  };

  return (
    <DatePicker
      locale={ja}
      className='w-full rounded border border-solid border-gray-250 bg-calendar bg-[center_right_12px] bg-no-repeat px-[16px] py-[10px] text-sm'
      selected={startDate}
      onChange={handleDateChange}
      placeholderText='YYYY-MM-DD'
      dateFormat='yyyy-MM-dd'
      onKeyDown={handleKeyDown}
    />
  );
};

const InputImageInline: React.FC<ImageUploaderProps> = ({ text, onImageUpload, onError }) => {
  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        onError(`ファイルサイズは ${MAX_FILE_SIZE / (1024 * 1024)}MB 以下でなければなりません。`);
        return;
      }

      const res = await articleApiClient.uploadThumbnail(file);
      handleApiResponse(
        res,
        (data) => {
          onImageUpload(data.url);
        },
        (errors) => {
          onError(firstError(errors.file));
        },
      );
    }
  };

  return (
    <label className='cursor-pointer text-xs text-primary-500 underline hover:no-underline'>
      <input
        className='sr-only appearance-none'
        type='file'
        accept='image/jpeg, image/jpg, image/png, image/gif'
        onChange={handleImageChange}
      />
      <span>{text}</span>
    </label>
  );
};

function InputPdfUpload({ children }: { children: React.ReactNode }) {
  return (
    <div className='flex items-center gap-2'>
      <img
        src={PdfIcon}
        alt=''
        width='24'
        height='24'
        loading='lazy'
      />
      <span className='text-xs'>{children}</span>
    </div>
  );
}

const InputPdf: React.FC<PdfUploaderProps> = ({ text, type, onPdfUpload, onError }) => {
  const handlePdfChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        onError(`ファイルサイズは ${MAX_FILE_SIZE / (1024 * 1024)}MB 以下でなければなりません。`);
        return;
      }

      const res = await articleApiClient.uploadPdf(file);
      handleApiResponse(
        res,
        (data) => {
          onPdfUpload(data.url, file.name);
        },
        (errors) => {
          onError(firstError(errors.file));
        },
      );
    }
  };

  if (type === 'text') {
    return (
      <label className='cursor-pointer text-xs text-primary-500 underline hover:no-underline'>
        <input
          className='sr-only appearance-none'
          type='file'
          accept='application/pdf'
          onChange={handlePdfChange}
        />
        <span>{text}</span>
      </label>
    );
  } else {
    return (
      <label className='grid cursor-pointer place-items-center rounded border border-solid border-primary-500 bg-white py-[10px] text-sm text-primary transition-opacity hover:opacity-70'>
        <input
          className='sr-only appearance-none'
          type='file'
          accept='application/pdf'
          onChange={handlePdfChange}
        />
        <span>{text}</span>
      </label>
    );
  }
};

function InputSearch() {
  return (
    <div className='flex rounded-[2px] border border-solid border-gray-400'>
      <input
        className='flex-1 px-[16px] py-[10px] text-sm'
        type='text'
        placeholder='キーワード検索'
      />
      <button
        className='grid w-[36px] place-items-center'
        type='button'
      >
        <img
          className='size-[20px]'
          src={SearchIcon}
          alt=''
          width='20'
          height='20'
        />
      </button>
    </div>
  );
}

const Input: React.FC<InputProps> = ({
  countChange,
  placeholder,
  onEnterPress,
  onChange,
  value, // 初期値を受け取るプロパティを追加
}) => {
  const [inputValue, setInputValue] = useState(value || ''); // 初期値を設定

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (countChange) {
      countChange(newValue.length);
    }
    if (onChange) {
      onChange(event);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onEnterPress) {
      event.preventDefault();
      onEnterPress(inputValue);
      setInputValue('');
    }
  };

  return (
    <input
      className='rounded border border-solid border-gray-250 px-[16px] py-[10px] text-sm'
      type='text'
      value={inputValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
    />
  );
};

const Select: React.FC<SelectProps> = ({ items, onSelect, initialValue }) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(initialValue);

  useEffect(() => {
    if (initialValue) {
      setSelectedValue(initialValue);
      if (onSelect) {
        onSelect(initialValue);
      }
    }
  }, [initialValue, onSelect]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    if (onSelect) {
      onSelect(value);
    }
  };

  return (
    <select
      onChange={handleChange}
      value={selectedValue}
      className='appearance-none rounded border border-solid border-gray-250 bg-down bg-[center_right_12px] bg-no-repeat px-[16px] py-[10px] text-sm'
    >
      <option value=''>選択してください</option>
      {items && items.length > 0 ? (
        items.map((option) => (
          <option
            key={option.id}
            value={option.id}
          >
            {option.name}
          </option>
        ))
      ) : (
        <option
          value=''
          disabled
        >
          データがありません
        </option>
      )}
    </select>
  );
};

const Switch = React.forwardRef<HTMLInputElement, CheckProps>(
  ({ onCheck, onUncheck, checked = false }, ref) => {
    const [isChecked, setIsChecked] = useState<boolean>(checked);

    useEffect(() => {
      setIsChecked(checked);
    }, [checked]);

    const handleChange = () => {
      setIsChecked((prev) => {
        const newCheckedState = !prev;
        setTimeout(() => {
          if (newCheckedState) {
            onCheck();
          } else {
            onUncheck();
          }
        }, 0);
        return newCheckedState;
      });
    };

    return (
      <label className='inline-flex cursor-pointer items-center'>
        <input
          ref={ref}
          type='checkbox'
          checked={isChecked}
          onChange={handleChange}
          className='peer sr-only'
        />
        <div className="peer relative h-[16px] w-[30px] rounded-full border border-solid border-black transition-colors after:absolute after:left-[2px] after:top-[3px] after:size-[8px] after:rounded-full after:bg-black after:transition-all after:content-[''] peer-checked:border-primary-500 peer-checked:bg-primary-500 peer-checked:after:left-[10px] peer-checked:after:translate-x-full peer-checked:after:bg-white"></div>
      </label>
    );
  },
);

Switch.displayName = 'Switch';

const TagGray: React.FC<TagProps> = ({ text, index, onClick }) => {
  return (
    <div className='flex min-w-0 items-center gap-[8px] rounded-full bg-gray-150 px-[16px] py-[6px]'>
      <div className='min-w-0 truncate'>
        <div className='truncate text-xs'>{text}</div>
      </div>
      <button
        className='shrink-0'
        type='button'
        onClick={() => onClick(index)}
      >
        <img
          src={CloseIcon}
          alt=''
          width='14'
          height='14'
          loading='lazy'
        />
      </button>
    </div>
  );
};

const TagPrimaryOutline: React.FC<TagProps> = ({ text, index, onClick }) => {
  return (
    <div
      key={index}
      className='flex min-w-0 items-center gap-[8px] rounded-full border border-solid border-gray-250 bg-white px-[16px] py-[6px]'
    >
      <div className='min-w-0 truncate'>
        <div className='truncate text-xs text-primary-400'>{text}</div>
      </div>
      <button
        className='shrink-0 p-0'
        type='button'
        onClick={() => onClick(index)}
      >
        <SvgIconClose
          color={brandColors['500']}
          style={{
            width: '14px',
            height: '14px',
          }}
        />
      </button>
    </div>
  );
};

interface TextareaProps {
  countChange?: (count: number) => void;
  onChange: (value: string) => void;
  value?: string;
  placeholder?: string;
  height?: string;
  error?: boolean;
}

const Textarea: React.FC<TextareaProps> = ({
  countChange,
  onChange,
  value,
  placeholder,
  height = '120px',
  error = false,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [currentHeight, setCurrentHeight] = useState(height);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (countChange) {
      countChange(newValue.length);
    }
    onChange(newValue);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();

    const startY = event.clientY;
    const startHeight = parseInt(currentHeight);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newHeight = Math.max(60, startHeight + deltaY); // 最小高さ60px
      setCurrentHeight(`${newHeight}px`);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    const startY = event.touches[0].clientY;
    const startHeight = parseInt(currentHeight);

    const handleTouchMove = (moveEvent: TouchEvent) => {
      const deltaY = moveEvent.touches[0].clientY - startY;
      const newHeight = Math.max(60, startHeight + deltaY); // 最小高さ60px
      setCurrentHeight(`${newHeight}px`);
    };

    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <div
      ref={containerRef}
      className='relative'
      style={{ height: currentHeight }}
    >
      <textarea
        ref={textareaRef}
        className={`size-full resize-none rounded border border-solid border-gray-250 p-[14px] text-base focus:shadow-none focus:outline-none ${
          error ? 'border-warning-500 bg-warning-200/70' : ''
        }`}
        value={inputValue}
        placeholder={placeholder}
        onChange={handleChange}
      ></textarea>

      {/* カスタムリサイズハンドラー - 2本の平行斜線 */}
      <div
        className={`custom-resize-handler absolute bottom-[2px] right-[2px] size-3 cursor-se-resize touch-none lg:size-4`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      />
    </div>
  );
};

export {
  ErrorMessage,
  Input,
  InputCalender,
  InputImageInline,
  InputPdf,
  InputPdfUpload,
  InputSearch,
  Select,
  Switch,
  TagGray,
  TagPrimaryOutline,
  Textarea,
};
